import React from 'react';
import {
  Calendar,
  Clock,
  Home,
  User,
  Car,
  AlertTriangle,
  Camera,
  Phone,
  Mail,
  AlertCircle,
  Coffee,
  Sandwich,
  Sun,
  Moon
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const getIconComponent = (iconName) => {
  const icons = {
    calendar: Calendar,
    clock: Clock,
    home: Home,
    user: User,
    car: Car,
    alert: AlertTriangle,
    camera: Camera,
    phone: Phone,
    mail: Mail,
    alertcircle: AlertCircle,
    coffee: Coffee,
    burger: Sandwich,
    sun: Sun,
    moon: Moon,
  };
  return icons[iconName] || Calendar;
};

const rowFmt = (row) => ({
  fontWeight: row.bold ? 700 : undefined,
  fontStyle: row.italic ? 'italic' : undefined,
});

const sectionStyle = (item) =>
  item.color ? { borderLeft: `5px solid ${item.color}`, paddingLeft: '6px' } : undefined;

// Generische Tabellen-Sektion (Tag / Calltime / CrewInfo)
function TableSection({ item, columns, textField, className }) {
  const Icon = getIconComponent(item.icon);
  const title = item.itemType === 'day' ? item.date : item.title;
  return (
    <div className={`${className} mb-8`} style={sectionStyle(item)}>
      <div className="print-section-title bg-slate-100 px-4 py-2 font-semibold text-slate-900 mb-2 flex items-center gap-2">
        <Icon className="h-4 w-4" />
        {title}
      </div>
      <table className="w-full border-collapse" style={{ tableLayout: 'fixed' }}>
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.id}
                className="border border-slate-300 bg-slate-50 px-2 py-1 text-left text-xs font-semibold"
                style={{ width: `${col.width}%` }}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {item.rows.map((row) => {
            const fmt = rowFmt(row);
            const lb = row.color ? `4px solid ${row.color}` : undefined;
            if (row.type === 'text') {
              return (
                <tr key={row.id}>
                  <td
                    colSpan={Math.max(1, columns.length)}
                    className="border border-slate-300 bg-slate-50 px-2 py-1 text-left font-semibold text-sm"
                    style={{ overflowWrap: 'break-word', wordWrap: 'break-word', whiteSpace: 'normal', borderLeft: lb, ...fmt }}
                  >
                    {row.text != null ? row.text : row[textField]}
                  </td>
                </tr>
              );
            }
            return (
              <tr key={row.id}>
                {columns.map((col, ci) => (
                  <td
                    key={col.id}
                    className="border border-slate-300 px-2 py-1 text-xs"
                    style={{ overflowWrap: 'break-word', wordWrap: 'break-word', whiteSpace: 'pre-wrap', ...fmt, ...(ci === 0 && lb ? { borderLeft: lb } : {}) }}
                  >
                    {row[col.id] || ''}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function TextBlock({ item }) {
  return (
    <div className="mb-8" style={sectionStyle(item)}>
      <div
        className="border border-slate-300 rounded p-3 text-sm text-slate-700"
        style={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}
      >
        {item.text || ''}
      </div>
    </div>
  );
}

function PrintView({ project }) {
  const allItems = [
    ...project.days.map((day) => ({ ...day, itemType: 'day' })),
    ...(project.calltimes || []).map((ct) => ({ ...ct, itemType: 'calltime' })),
    ...(project.crewInfos || []).map((ci) => ({ ...ci, itemType: 'crewInfo' })),
    ...(project.textBlocks || []).map((tb) => ({ ...tb, itemType: 'textBlock' })),
  ].sort((a, b) => (a.position || 0) - (b.position || 0));

  const dayColumns = (project.columns && project.columns.length)
    ? project.columns
    : [
        { id: 'time', label: project.column_headers?.time || 'Time', width: 15 },
        { id: 'scene', label: project.column_headers?.scene || 'Scene', width: 15 },
        { id: 'location', label: project.column_headers?.location || 'Location', width: 23 },
        { id: 'cast', label: project.column_headers?.cast || 'Cast', width: 23 },
        { id: 'notes', label: project.column_headers?.notes || 'Notes', width: 24 },
      ];

  const calltimeColumns = (item) =>
    (item.columns && item.columns.length)
      ? item.columns
      : [
          { id: 'time', label: item.headers?.time || 'Time', width: 18 },
          { id: 'name', label: item.headers?.name || 'Name', width: 82 },
        ];

  const crewColumns = (item) =>
    (item.columns && item.columns.length)
      ? item.columns
      : [
          { id: 'name', label: item.headers?.name || 'Name', width: 25 },
          { id: 'crew', label: item.headers?.crew || 'Crew', width: 20 },
          { id: 'info', label: item.headers?.info || 'Info', width: 55 },
        ];

  const renderItem = (item) => {
    if (item.itemType === 'day') {
      return <TableSection key={item.id} item={item} columns={dayColumns} textField="notes" className="schedule-day" />;
    }
    if (item.itemType === 'calltime') {
      return <TableSection key={item.id} item={item} columns={calltimeColumns(item)} textField="name" className="calltime-section" />;
    }
    if (item.itemType === 'crewInfo') {
      return <TableSection key={item.id} item={item} columns={crewColumns(item)} textField="name" className="crew-info-section" />;
    }
    return <TextBlock key={item.id} item={item} />;
  };

  return (
    <div className="print-view p-8" style={{ maxWidth: '210mm', margin: '0 auto' }}>
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">{project.name}</h1>
          {project.notes && (
            <p className="text-sm text-slate-600 whitespace-pre-wrap">{project.notes}</p>
          )}
        </div>
        {project.logo_url && (
          <img
            src={`${BACKEND_URL}${project.logo_url}`}
            alt="Logo"
            className="max-w-[150px] max-h-[80px] object-contain ml-4"
          />
        )}
      </div>

      {allItems.map((item) => renderItem(item))}
    </div>
  );
}

export default PrintView;
